<script setup lang="ts">
import { provide, ref, useSlots } from "vue";
import { nameInjectionKey } from "../keys";
const slot = useSlots();

const props = withDefaults(defineProps<{
    name: string,
    type?: string,
    label?: string,
    subLabel?: string,
    tooltip?: string,
    includePadding?: boolean,
    class?: string,
    labelClass?: string,
    inputClass?: string,
}>(), {
    type: "",
    label: "",
    subLabel: "",
    tooltip: "",
    includePadding: true,
    class: "",
    labelClass: "col-12 col-form-label",
    inputClass: "col-12 col-md-11 col-lg-11",
});

const showTooltip = ref(false);

provide(nameInjectionKey, props.name);

</script>

<template>
    <div
        :id="`${name}-container`"
        :class="props.class"
    >
        <fieldset
            v-if="props.type === 'group'"
            class="fieldset"
            :aria-labelledby="`${name}-id`"
        >
            <div
                class="row position-relative"
                :class="{
                    'form-group': props.includePadding
                }"
            >
                <!-- name prop must come from the caller -->
                <legend
                    :id="`${name}-id`"
                    :class="props.labelClass"
                >
                    <span
                        :class="['col-form-label', {'hasTooltip': props.tooltip || slot.tooltip}]"
                    >
                        {{ props.label }}
                        <slot name="label" />
                    </span>
                    <span
                        v-if="props.subLabel"
                        class="sublabel"
                    >
                        {{ props.subLabel }}
                    </span>
                </legend>
                <div :class="props.inputClass">
                    <div class="row question-group">
                        <slot />
                    </div>
                </div>
                <div
                    v-if="tooltip || slot.tooltip"
                    class="order-last col-1 tooltip-container"
                >
                    <div
                        data-placement="right"
                        title="Click for help text!"
                        class="w-25"
                    >
                        <button
                            aria-label="Click for help text"
                            class="btn btn-circular font-weight-bold pulse"
                            type="button"
                            aria-expanded="false"
                            :aria-controls="name + '_collapse'"
                            @click="showTooltip = !showTooltip"
                        >
                            ?
                        </button>
                    </div>
                </div>
            </div>
        </fieldset>
        <div
            v-else
            class="row position-relative"
            :class="{
                'form-group': props.includePadding
            }"
        >
            <!-- name prop must come from the caller -->
            <label
                :for="name"
                :class="props.labelClass"
            >
                <span
                    :class="['col-form-label', {'hasTooltip': props.tooltip || slot.tooltip}]"
                >
                    {{ props.label }}
                    <slot name="label" />
                </span>
                <span
                    v-if="props.subLabel"
                    class="sublabel"
                >
                    {{ props.subLabel }}
                </span>
            </label>
            <div :class="props.inputClass">
                <div class="row">
                    <slot />
                </div>
            </div>
            <div
                v-if="tooltip || slot.tooltip"
                class="order-last col-1 tooltip-container"
            >
                <div
                    data-placement="right"
                    title="Click for help text!"
                    class="w-25"
                >
                    <button
                        aria-label="Click for help text"
                        class="btn btn-circular font-weight-bold pulse"
                        type="button"
                        aria-expanded="false"
                        :aria-controls="name + '_collapse'"
                        @click="showTooltip = !showTooltip"
                    >
                        ?
                    </button>
                </div>
            </div>
        </div>
        <Transition mode="out-in">
            <div
                v-if="showTooltip"
                :id="name+ '_collapse'"
                class="show"
            >
                <div
                    class="alert alert-warning alert-dismissible fade show mb-4 question-tooltip"
                    role="alert"
                >
                    <span
                        v-if="tooltip"
                        v-html="tooltip"
                    />

                    <slot
                        v-if="!tooltip"
                        name="tooltip"
                    />

                    <button
                        type="button"
                        class="btn-close"
                        aria-label="Close"
                        @click="showTooltip = false"
                    />
                </div>
            </div>
        </Transition>
    </div>
</template>

<style>
.v-enter-active,
.v-leave-active {
    transition: opacity 0.2s ease;
}
.sublabel {
    font-size: 12px;
    color: #757373;
    display: block;
    margin-top: -2px;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
}
</style>
